import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, from, switchMap, throwError } from 'rxjs';
import {
  isAnonymousPublicPath,
  isPublicShareApiUrl,
} from './anonymous-public-path';
import { AuthSessionService } from './auth-session.service';
import { MandatoryFeedbackService } from './mandatory-feedback.service';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthSessionService);
  const router = inject(Router);
  const mandatoryFeedback = inject(MandatoryFeedbackService);
  const accessToken = auth.getAccessToken();
  const skipAuthHeader = isPublicShareApiUrl(req.url);
  const authReq =
    accessToken && !skipAuthHeader
      ? req.clone({ setHeaders: { Authorization: `Bearer ${accessToken}` } })
      : req;

  return next(authReq).pipe(
    catchError((error) => {
      if (
        error instanceof HttpErrorResponse &&
        error.status === 400 &&
        error.error?.type === 'feedback-required' &&
        !req.url.includes('/feedback')
      ) {
        void mandatoryFeedback.openFeedbackDialog(error.error?.metadata);
        return throwError(() => error);
      }

      if (
        error instanceof HttpErrorResponse &&
        error.status === 400 &&
        error.error?.type === 'account-deleted'
      ) {
        auth.clearSession();
        void router.navigate(['/learner/login']);
        return throwError(() => error);
      }

      if (!(error instanceof HttpErrorResponse) || error.status !== 401) {
        return throwError(() => error);
      }

      // Do not recurse for auth endpoints, public pages/APIs, or an already retried request.
      const onAnonymousPage =
        isAnonymousPublicPath(router.url) ||
        (typeof window !== 'undefined' &&
          isAnonymousPublicPath(window.location.pathname));

      if (
        skipAuthHeader ||
        onAnonymousPage ||
        req.url.includes('/auth/login') ||
        req.url.includes('/auth/forgot-password') ||
        req.url.includes('/auth/reset-password') ||
        req.url.includes('/auth/refresh') ||
        req.headers.has('x-refresh-retry')
      ) {
        return throwError(() => error);
      }

      return from(auth.refreshAccessToken(true)).pipe(
        switchMap((refreshed) => {
          if (!refreshed) {
            return throwError(() => error);
          }

          const nextToken = auth.getAccessToken();
          if (!nextToken) {
            return throwError(() => error);
          }

          return next(
            req.clone({
              setHeaders: {
                Authorization: `Bearer ${nextToken}`,
                'x-refresh-retry': '1',
              },
            }),
          );
        }),
        catchError(() => throwError(() => error)),
      );
    }),
  );
};
